import { installedApps } from "@norn/widget-renderer/model"
import { Link } from "@tanstack/react-router"
import { Banknote, Camera, ChevronRight, CircleAlert, Clock3, type LucideIcon, PackageOpen, ShoppingBag, Users } from "lucide-react"
import { type ReactNode, useState } from "react"
import type { Schemas } from "@norn/sdk"
import { Section } from "@/components/ui/section"
import { Skeleton } from "@/components/ui/skeleton"
import { KpiTile } from "@/components/ui/stat-strip"
import type { MessageKey } from "@/i18n/en"
import type { Scope } from "@/lib/connection"
import type { DefinitionSet } from "@/lib/defs"
import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { OrdersNeedYou } from "@/workbook/needs-you"
import { usePrincipal } from "@/widget/runtime"
import { type ObjectResponse, useLiveObjects } from "./hooks"
import { HiddenForRole, useLens } from "./lens"
import type { Ontology, TypeMeta } from "./ontology"
import { CreateCommands, Receipt, RecordCommands, type RecordCommand } from "./record-commands"
import { FieldValue, isMasked, type Masked, maskMarkings, numericText, RefusalNote, StatusPill, useFormatters } from "./values"

/**
 * Business-mode home: the work people came to do, across apps. Every number and queue item is an
 * authorized read of real records; hidden values stay hidden (the cell masks them), and a KPI that
 * aggregates hidden fields says so instead of revealing them.
 */

const LIST = 200
const minute = () => 60_000

type Values = Record<string, unknown>
const valuesOf = (o: ObjectResponse) => o.values as Values
const maskedOf = (o: ObjectResponse) => o.masked as Masked

/** A queue source: one record type, which records need someone, and the app the work belongs to. */
interface QueueSource {
  readonly type: string
  readonly kind: "overdue" | "unmatched" | "lowStock" | "approval"
  readonly app: AppId
  readonly needs: (values: Values, masked: Masked) => boolean
}

const readable = (masked: Masked, ...ids: string[]) => ids.every((id) => !isMasked(masked, id))
const int = (v: unknown) => {
  const text = numericText(v)
  return text === null ? null : Number(text)
}

const QUEUE: readonly QueueSource[] = [
  {
    type: "showcase.SalesInvoice",
    kind: "overdue",
    app: "cash",
    needs: (v, m) => readable(m, "status") && v.status === "Overdue",
  },
  {
    type: "showcase.ChannelOrder",
    kind: "unmatched",
    app: "sell",
    needs: (v, m) => readable(m, "match_status", "review_status") && v.match_status === "unmatched" && v.review_status !== "closed",
  },
  {
    type: "showcase.Bin",
    kind: "lowStock",
    app: "inventory",
    needs: (v, m) => {
      if (!readable(m, "actual_qty", "reorder_level")) return false
      const have = int(v.actual_qty)
      const floor = int(v.reorder_level)
      return have !== null && floor !== null && have < floor
    },
  },
  {
    type: "commerce.SalesOrder", kind: "approval", app: "sell",
    needs: (v, m) => readable(m, "status") && (v.status === "submitted" || v.status === "on_hold"),
  },
]

type AppId = "sell" | "cash" | "crm" | "studio" | "inventory"

const APP_META: Record<AppId, { label: MessageKey; hint: MessageKey; icon: LucideIcon; match: RegExp; fallbackType?: string }> = {
  sell: { label: "ac.app.sell", hint: "ac.app.sellHint", icon: ShoppingBag, match: /(^|[._])(sell|pos|sales)([._]|$)/, fallbackType: "showcase.SalesInvoice" },
  cash: { label: "ac.app.cash", hint: "ac.app.cashHint", icon: Banknote, match: /cash/, fallbackType: "showcase.SalesInvoice" },
  crm: { label: "ac.app.crm", hint: "ac.app.crmHint", icon: Users, match: /crm/, fallbackType: "party.Customer" },
  studio: { label: "ac.app.studio", hint: "ac.app.studioHint", icon: Camera, match: /studio/ },
  inventory: { label: "ac.app.inventory", hint: "ac.app.inventoryHint", icon: PackageOpen, match: /stock|inventory/, fallbackType: "showcase.Bin" },
}

/** Where an app tile opens: its installed app's entry page, else its business area, else its main records. */
type AppTarget =
  | { readonly kind: "app"; readonly appId: string; readonly pageId: string }
  | { readonly kind: "area"; readonly areaId: string }
  | { readonly kind: "records"; readonly objectType: string }

export function appTarget(id: AppId, set: DefinitionSet | undefined, ontology: Ontology | undefined): AppTarget | null {
  const meta = APP_META[id]
  if (set) {
    const app = installedApps(set).find((a) => meta.match.test(a.appId))
    const entry = app?.ux.app?.entryPageId ?? app?.ux.pages.find((p) => !p.contextParams.some((c) => c.required))?.pageId
    if (app && entry) return { kind: "app", appId: app.appId, pageId: entry }
  }
  const area = ontology?.areas.find((a) => !a.core && meta.match.test(a.id))
  if (area) return { kind: "area", areaId: area.id }
  if (meta.fallbackType && ontology?.types.has(meta.fallbackType)) return { kind: "records", objectType: meta.fallbackType }
  return null
}

function TargetLink({ scope, target, className, children, label }: { scope: Scope; target: AppTarget; className?: string; children: ReactNode; label?: string }) {
  if (target.kind === "app")
    return (
      <Link to="/$orgId/$environmentId/apps/$appId/$pageId" params={{ ...scope, appId: target.appId, pageId: target.pageId }} search={{}} aria-label={label} className={className}>
        {children}
      </Link>
    )
  if (target.kind === "area")
    return (
      <Link to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: target.areaId }} aria-label={label} className={className}>
        {children}
      </Link>
    )
  return (
    <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: target.objectType }} aria-label={label} className={className}>
      {children}
    </Link>
  )
}

/** Whether this cell carries the app-center sources (the synthetic showcase does; other cells fall back). */
export function hasAppCenter(ontology: Ontology | undefined): boolean {
  return Boolean(ontology && QUEUE.some((q) => ontology.types.has(q.type)))
}

// ---------------------------------------------------------------- KPIs

/** A board metric row by key (materialized server-side by the cell's service principal). */
const BOARD_TYPE = "showcase.MetricBoard"

function useBoardRow(scope: Scope, key: string) {
  const list = useLiveObjects(scope, BOARD_TYPE, 50, minute)
  return { list, row: list.data?.items.find((r) => r.key === key) }
}

/**
 * A KPI computed by the cell over fields the reader may not see. The aggregate is readable; the
 * operands are not. When the cell withholds the figure (its masked indicator), say why.
 */
function UsedNotRevealed({ scope, metric, label, marking, operandType }: { scope: Scope; metric: string; label: MessageKey; marking: string; operandType: TypeMeta | undefined }) {
  const t = useT()
  const fmt = useFormatters()
  const lens = useLens()
  const { list, row } = useBoardRow(scope, metric)
  const masked = row ? maskedOf(row) : undefined
  const withheld = Boolean(row && isMasked(masked, "value"))
  const value = row && !withheld ? numericText(valuesOf(row).value) : null
  const markings = withheld ? maskMarkings(masked, "value") : []
  const markingLabel = lens?.catalog.get(marking)?.label ?? marking
  return (
    <KpiTile
      label={t(label)}
      value={
        list.isPending ? (
          <Skeleton className="h-8 w-28" />
        ) : withheld ? (
          <HiddenForRole markings={markings.length ? markings : [marking]} className="align-middle" />
        ) : value !== null ? (
          fmt.money(value, "EGP")
        ) : (
          "—"
        )
      }
      sub={
        list.isError ? (
          t("ws.home.metricUnavailable")
        ) : withheld ? (
          t("ac.kpi.usesHidden", { label: markingLabel.toLowerCase(), type: operandType?.pluralLabel.toLowerCase() ?? "" })
        ) : row ? (
          t("ac.kpi.fromRecords", { type: operandType?.pluralLabel.toLowerCase() ?? "" })
        ) : (
          t("ws.home.metricPending")
        )
      }
    />
  )
}

function CountKpi({ scope, source, label, ontology }: { scope: Scope; source: QueueSource; label: MessageKey; ontology: Ontology }) {
  const t = useT()
  const fmt = useFormatters()
  const list = useLiveObjects(scope, source.type, LIST, minute)
  const items = (list.data?.items ?? []).filter((o) => source.needs(valuesOf(o), maskedOf(o)))
  const type = ontology.types.get(source.type)
  return (
    <KpiTile
      label={t(label)}
      value={list.isPending ? <Skeleton className="h-8 w-16" /> : list.isError ? "—" : fmt.number(items.length)}
      sub={list.isError ? t("ws.home.listUnavailable", { name: type?.pluralLabel ?? source.type }) : t(`ac.kpi.${source.kind}Sub`, { count: items.length, more: list.data?.nextCursor ? "+" : "" })}
      subTone={items.length ? "down" : "neutral"}
    />
  )
}

export function AppCenterKpis({ scope, ontology }: { scope: Scope; ontology: Ontology }) {
  const lines = ontology.types.get("showcase.SalesInvoiceItem")
  const teams = ontology.types.get("showcase.SalesTeam")
  const [overdue, unmatched] = [QUEUE[0], QUEUE[1]]
  // The margin and commission figures are board metrics; a cell without the board shows only the counts.
  const board = ontology.types.has(BOARD_TYPE)
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {board ? <UsedNotRevealed scope={scope} metric="gross_margin" label="ac.kpi.grossMargin" marking="finance.margin" operandType={lines} /> : null}
      {board ? <UsedNotRevealed scope={scope} metric="sales_commission" label="ac.kpi.commission" marking="hr.compensation" operandType={teams} /> : null}
      {overdue && ontology.types.has(overdue.type) ? <CountKpi scope={scope} source={overdue} label="ac.kpi.overdue" ontology={ontology} /> : null}
      {unmatched && ontology.types.has(unmatched.type) ? <CountKpi scope={scope} source={unmatched} label="ac.kpi.unmatched" ontology={ontology} /> : null}
    </div>
  )
}

// ----------------------------------------------------------- Needs you

function QueueGroup({ scope, source, ontology, set }: { scope: Scope; source: QueueSource; ontology: Ontology; set: DefinitionSet | undefined }) {
  const t = useT()
  const list = useLiveObjects(scope, source.type, LIST, minute)
  const [completed, setCompleted] = useState<ReadonlyMap<string, { version: number; revision: number }>>(new Map())
  const [receipt, setReceipt] = useState<{ title: string; accepted: Schemas["ActionAccepted"] } | null>(null)
  const type = ontology.types.get(source.type)
  if (!type) return null
  if (list.isPending) return <Skeleton className="h-20 w-full rounded-xl" />
  if (list.isError) return <RefusalNote error={list.error} title={t("ws.home.listUnavailable", { name: type.pluralLabel })} />
  const items = list.data.items.filter((o) => {
    const pending = completed.get(o.key)
    // Suppress only the pre-commit snapshot. A successful fresh read is authoritative,
    // including a restored seed whose object version is lower than the accepted one.
    return (!pending || list.readRevision !== pending.revision || o.version >= pending.version) && source.needs(valuesOf(o), maskedOf(o))
  })
  if (!items.length && !receipt) return null
  const app = APP_META[source.app]
  const target = appTarget(source.app, set, ontology)
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 px-1">
        <app.icon className="size-3.5 text-muted-foreground" aria-hidden />
        <h3 className="text-[13px] font-medium">{t(`ac.queue.${source.kind}`, { count: items.length })}</h3>
        {target ? (
          <TargetLink scope={scope} target={target} className="ms-auto text-xs text-muted-foreground hover:text-foreground">
            {t("ac.queue.openIn", { app: t(app.label) })}
          </TargetLink>
        ) : null}
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {items.slice(0, 5).map((o) => (
          <QueueRow key={o.key} scope={scope} type={type} object={o} source={source} set={set} onCommitted={(accepted, command) => {
            // Hide the stale queue snapshot until the accepted version is read.
            const written = accepted.receipt.written.find((w) => w.objectType === o.objectType && w.key === o.key)
            if (written) setCompleted((previous) => new Map([...previous, [o.key, { version: written.version, revision: list.readRevision }]]))
            setReceipt({ title: command.title, accepted })
          }} />
        ))}
      </ul>
      {receipt ? <Receipt scope={scope} title={receipt.title} accepted={receipt.accepted} /> : null}
      {items.length > 5 ? (
        <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: source.type }} className="block px-1 pt-1 text-xs text-muted-foreground hover:text-foreground">
          {t("ac.queue.more", { count: items.length - 5 })}
        </Link>
      ) : null}
    </div>
  )
}

function QueueRow({ scope, type, object, source, set, onCommitted }: { scope: Scope; type: TypeMeta; object: ObjectResponse; source: QueueSource; set: DefinitionSet | undefined; onCommitted: (accepted: Schemas["ActionAccepted"], command: RecordCommand) => void }) {
  const t = useT()
  const fmt = useFormatters()
  const principal = usePrincipal(scope)
  const v = valuesOf(object)
  const masked = maskedOf(object)
  const field = (id: string) => type.fields.find((f) => f.id === id)
  const title = typeof v.title === "string" && !isMasked(masked, "title") ? v.title : object.key
  const line =
    source.kind === "overdue"
      ? t("ac.queue.overdueDetail", { key: object.key, due: typeof v.due_date === "string" ? fmt.date(v.due_date) : "—" })
      : source.kind === "unmatched"
        ? typeof v.match_note === "string" && !isMasked(masked, "match_note") ? v.match_note : t("ac.queue.unmatchedDetail")
        : source.kind === "approval" ? t("ac.queue.approvalDetail")
        : t("ac.queue.lowStockDetail", { have: fmt.number(String(v.actual_qty ?? "—")), floor: fmt.number(String(v.reorder_level ?? "—")) })
  const amount = source.kind === "overdue" ? field("outstanding_amount") : source.kind === "unmatched" ? field("total_price") : undefined
  return (
    <li>
      <Link
        to="/$orgId/$environmentId/records/$objectType/$key"
        params={{ ...scope, objectType: object.objectType, key: object.key }}
        className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3.5 py-2.5 text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
      >
        <span className="min-w-0">
          <span dir="auto" className="block truncate font-medium">{title}</span>
          <span dir="auto" className="block truncate text-xs text-muted-foreground">{line}</span>
        </span>
        <span className="text-xs">{amount ? <FieldValue field={amount} values={v} masked={masked} type={type} /> : null}</span>
        <ChevronRight className="size-4 text-subtle-foreground rtl:-scale-x-100" aria-hidden />
      </Link>
      {set ? <div className="px-3.5 pb-3"><RecordCommands scope={scope} actor={principal.status === "ready" ? principal.actor : null} type={type} objectKey={object.key} version={object.version} values={v} masked={masked} artifacts={set.artifacts} onCommitted={onCommitted} /></div> : null}
    </li>
  )
}

export function NeedsYou({ scope, ontology, set }: { scope: Scope; ontology: Ontology; set: DefinitionSet | undefined }) {
  const t = useT()
  return (
    <Section title={t("ac.queue.title")} description={t("ac.queue.description")}>
      <div className="space-y-5">
        <OrdersNeedYou scope={scope} ontology={ontology} />
        {QUEUE.filter((q) => ontology.types.has(q.type)).map((source) => (
          <QueueGroup key={source.type} scope={scope} source={source} ontology={ontology} set={set} />
        ))}
      </div>
    </Section>
  )
}

// ---------------------------------------------------------- Sync health

const SYNC_TONE: Record<string, string> = { healthy: "bg-[var(--success)]", delayed: "bg-[var(--warning)]", failing: "bg-destructive" }

function ago(iso: string, locale: string): string {
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return iso
  const minutes = Math.round((then - Date.now()) / 60_000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
  if (Math.abs(minutes) < 60) return rtf.format(minutes, "minute")
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 48) return rtf.format(hours, "hour")
  return rtf.format(Math.round(hours / 24), "day")
}

export function SyncHealth({ scope, ontology }: { scope: Scope; ontology: Ontology }) {
  const t = useT()
  const fmt = useFormatters()
  const list = useLiveObjects(scope, "showcase.SourceSync", 20, minute)
  const type = ontology.types.get("showcase.SourceSync")
  if (!type) return null
  return (
    <Section title={t("ac.sync.title")} bordered>
      {list.isPending ? (
        <Skeleton className="m-4 h-24" />
      ) : list.isError ? (
        <RefusalNote error={list.error} title={t("ws.home.listUnavailable", { name: type.pluralLabel })} className="m-3" />
      ) : (
        <ul className="divide-y divide-border">
          {list.data.items.map((o) => {
            const v = valuesOf(o)
            const status = typeof v.status === "string" ? v.status : null
            const statusField = type.fields.find((f) => f.id === "status")
            return (
              <li key={o.key}>
                <Link to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: o.objectType, key: o.key }} className="block space-y-1 px-5 py-3 outline-none hover:bg-muted/40 focus-visible:bg-muted/40">
                  <span className="flex items-center gap-2 text-sm">
                    <span aria-hidden className={cn("size-2 shrink-0 rounded-full", SYNC_TONE[status ?? ""] ?? "bg-muted-foreground")} />
                    <span dir="auto" className="min-w-0 flex-1 truncate font-medium">{typeof v.title === "string" ? v.title : o.key}</span>
                    {status ? <StatusPill value={status} field={statusField} type={type} /> : null}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 ps-4 text-xs text-muted-foreground">
                    {typeof v.last_synced_at === "string" ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock3 className="size-3" aria-hidden />
                        {ago(v.last_synced_at, fmt.tag)}
                      </span>
                    ) : null}
                    <span className="tabular-nums">{t("ac.sync.records", { count: Number(numericText(v.records_count) ?? 0), value: fmt.number(String(v.records_count ?? 0)) })}</span>
                    {Number(numericText(v.rejected_count) ?? 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[var(--warning)] tabular-nums">
                        <CircleAlert className="size-3" aria-hidden />
                        {t("ac.sync.rejected", { value: fmt.number(String(v.rejected_count)) })}
                      </span>
                    ) : null}
                  </span>
                  {typeof v.note === "string" ? <span dir="auto" className="block ps-4 text-xs text-subtle-foreground">{v.note}</span> : null}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

// --------------------------------------------------------------- Apps

const TILES: readonly AppId[] = ["sell", "cash", "crm", "studio"]

export function AppTiles({ scope, ontology, set }: { scope: Scope; ontology: Ontology | undefined; set: DefinitionSet | undefined }) {
  const t = useT()
  const orderType = ontology?.types.get("commerce.SalesOrder")
  const tiles = TILES.flatMap((id) => {
    const target = appTarget(id, set, ontology)
    return target ? [{ id, target, meta: APP_META[id] }] : []
  })
  if (!tiles.length) return null
  return (
    <section aria-labelledby="ac-apps" className="space-y-3">
      <h2 id="ac-apps" className="text-[17px] leading-tight font-medium">
        {t("ac.apps.title")}
      </h2>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map(({ id, target, meta }) => (
          <TargetLink
            key={id}
            scope={scope}
            target={target}
            className="group flex min-h-28 flex-col justify-between gap-4 rounded-2xl border border-border bg-card p-4 outline-none transition-colors hover:border-foreground/30 focus-visible:ring-3 focus-visible:ring-ring/30"
          >
            <span className="flex size-9 items-center justify-center rounded-xl bg-muted text-foreground">
              <meta.icon className="size-4.5" aria-hidden />
            </span>
            <span className="space-y-0.5">
              <span className="flex items-center gap-1 text-sm font-medium">
                {t(meta.label)}
                <ChevronRight className="size-3.5 text-subtle-foreground transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100" aria-hidden />
              </span>
              <span className="block text-xs text-muted-foreground">{t(meta.hint)}</span>
            </span>
          </TargetLink>
        ))}
      </div>
      {orderType && set ? <CreateCommands scope={scope} type={orderType} artifacts={set.artifacts} ontology={ontology} /> : null}
    </section>
  )
}

